import { useModal } from '@adserve/adserve-react-components';
import React, { createContext, useState, useCallback } from 'react';
import { okMessageButtonStyle } from '../components/Campaign/CampaignStyles';
import { BusinessTypes, OffairStatuses, QuoteStatusEnum } from '../constants/Enums';
import { ExpireQuote, GetCampaignPropertyStats } from '../services/CampaignsDataService';

const AirtimePlanningContext = createContext();

const AirtimePlanningProvider = ({ children }) => {
    const { addMessage } = useModal();
    const [reservedUntil, setReservedUntil] = useState(null);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [planIsInvalid, setPlanIsInvalid] = useState(false);
    const [offairActivities, setOffairActivities] = useState([]);
    // const [selectedProperties, setSelectedProperties] = useState([]);
    const [campaignProperties, setCampaignProperties] = useState([]);
    const [campaignPackages, setCampaignPackages] = useState([]);
    const [campaignBursts, setCampaignBursts] = useState([]);
    const [isReserved, setIsReserved] = useState(true);
    const [quotes, setQuotes] = useState([]);
    const [journeyBarSteps, setJourneyBarSteps] = useState([]);
    const [isCampaignCompleted, setIsCampaignCompleted] = useState(false);
    const [currentCampaignVersionId, setCurrentCampaignVersionId] = useState(window.emptyGuid);
    const [offairChanges, setOffairChanges] = useState(false);
    const [propertyStats, setPropertyStats] = useState([]);
    const [propertyTotals, setPropertyTotals] = useState({});
    const [disableBackButton, setDisableBackButton] = useState(true);
    const [isPropertyStatsLoaded, setIsPropertyStatsLoaded] = useState(false);
    const [billingAccountChanged, setBillingAccountChanged] = useState(false);

    const isRunningPropertySelection = (campaign, propSelection) => {
        return campaign.versionNumber > 0 && new Date(Date.parse(propSelection?.startDate)) <= new Date();
    };
    const isCompletedPropertySelection = (campaign, propSelection) => {
        const status = campaign.versionNumber > 0 && new Date(Date.parse(propSelection?.endDate)) <= new Date();

        // this logic should be transferred to back end as cut off dates are not being taken into account
        return status;
    };

    const canQuote = (campaign) => {
        // should this be more explicit about business types to reduce chance of breakage
        // be careful that any changes do not affect other business types

        if (campaign.businessType.toLowerCase() === BusinessTypes.AIRTIME.name.toLowerCase() && planIsInvalid) {
            return false;
        }

        if (campaign.businessType.toLowerCase() === BusinessTypes.AIRTIMEBURST.name.toLowerCase() && planIsInvalid) {
            return false;
        }

        if (
            campaign.businessType.toLowerCase() === BusinessTypes.OFFAIRS.name.toLowerCase() ||
            campaign.businessType.toLowerCase() === BusinessTypes.DIGITAL.name.toLowerCase()
        ) {
            if (
                offairActivities.every(
                    (oa) => oa.quoteId && oa.quoteId !== window.emptyGuid && oa.status === OffairStatuses.CANCELLED
                )
            ) {
                return false;
            }
        }

        if (
            currentPlan?.quoteId &&
            currentPlan?.quoteId !== window.emptyGuid &&
            quotes.some((q) => q.statusName === 'Booked') &&
            (campaign.businessType.toLowerCase() === BusinessTypes.AIRTIMEBURST.name.toLowerCase() ||
                offairActivities.every((oa) => oa.quoteId !== window.emptyGuid)) &&
            (campaign.businessType.toLowerCase() !== BusinessTypes.SANDP.name.toLowerCase() || !propertyStats?.length) &&
            !billingAccountChanged
        ) {
            // Quote already booked not yet rengotiated
            return false;
        }
        if (
            !currentPlan &&
            (campaign.businessType.toLowerCase() === BusinessTypes.AIRTIMEBURST.name.toLowerCase() ||
                offairActivities.every(
                    (oa) => oa.quoteId && oa.quoteId !== window.emptyGuid && oa.status === OffairStatuses.CANCELLED
                )) &&
            !offairChanges &&
            (campaign.businessType.toLowerCase() !== BusinessTypes.SANDP.name.toLowerCase() || !propertyStats?.length)
        ) {
            // Nothing to be quoted
            return false;
        }
        return true;
    };

    const fetchCampaignPropertyStats = useCallback(async (campaignId) => {
        setIsPropertyStatsLoaded(false);
        try {
            const data = await GetCampaignPropertyStats(campaignId);
            return data;
        } finally {
            setIsPropertyStatsLoaded(true);
        }
    }, []);

    const showInvalidatePlanMessage = (component, setFocus = true) => {
        if (currentPlan && !planIsInvalid && !isCampaignCompleted) {
            addMessage({
                message: window.translate(
                    'This has invalidated the current plan.  You will need to replan if you wish to quote for this campaign.'
                ),
                buttons: [
                    {
                        text: window.translate('Ok'),
                        style: okMessageButtonStyle,
                        callback: async () => {
                            setPlanIsInvalid(true);
                            if (
                                currentPlan.quoteId && // ASSP-7530 Nothing to invalidate if null or empty guid
                                currentPlan.quoteId !== '00000000-0000-0000-0000-000000000000' // ASSP-7530 Null window.emptyGuid)
                            ) {
                                ExpireQuote(currentPlan.quoteId);
                            }
                            setFocus && component.focus();
                        }
                    }
                ]
            });
        }
    };

    const hasAirtimeExpired = () => {
        if (
            currentPlan?.quote === null ||
            currentPlan?.quote === undefined ||
            currentPlan?.quote?.status === QuoteStatusEnum.RESERVED ||
            currentPlan?.quote?.status === QuoteStatusEnum.PLANNED
        ) {
            const dt = new Date();
            // Handling an empty plan (edge case where a plan has been invalidated and saved)
            const reservedUntil = new Date(
                currentPlan?.reservedUntil instanceof Date
                    ? currentPlan?.reservedUntil.getFullYear() !== 1
                        ? currentPlan?.reservedUntil
                        : undefined
                    : undefined
            );
            const remaining = (reservedUntil - dt) / 1000;

            return remaining <= 0;
        }

        return false;
    };

    const contextDispose = () => {
        setReservedUntil(null);
        setCurrentPlan(null);
        setPlanIsInvalid(false);
        setOffairActivities([]);
        // setSelectedProperties([]);
        setCampaignProperties([]);
        setCampaignPackages([]);
        setCampaignBursts([]);
        setIsReserved(true);
        setQuotes([]);
        setJourneyBarSteps([]);
        setIsCampaignCompleted(false);
        setCurrentCampaignVersionId(window.emptyGuid);
        setOffairChanges(false);
        setIsPropertyStatsLoaded(false);
    };

    return (
        <AirtimePlanningContext.Provider
            value={{
                hasAirtimeExpired,
                canQuote,
                reservedUntil,
                setReservedUntil,
                currentPlan,
                setCurrentPlan,
                planIsInvalid,
                setPlanIsInvalid,
                offairActivities,
                setOffairActivities,
                campaignProperties,
                setCampaignProperties,
                campaignPackages,
                setCampaignPackages,
                campaignBursts,
                setCampaignBursts,
                isReserved,
                setIsReserved,
                quotes,
                setQuotes,
                journeyBarSteps,
                setJourneyBarSteps,
                // selectedProperties,
                // setSelectedProperties,
                showInvalidatePlanMessage,
                isCampaignCompleted,
                setIsCampaignCompleted,
                currentCampaignVersionId,
                setCurrentCampaignVersionId,
                setOffairChanges,
                contextDispose,
                propertyStats,
                setPropertyStats,
                propertyTotals,
                setPropertyTotals,
                isRunningPropertySelection,
                isCompletedPropertySelection,
                disableBackButton,
                setDisableBackButton,
                isPropertyStatsLoaded,
                setIsPropertyStatsLoaded,
                fetchCampaignPropertyStats,
                billingAccountChanged,
                setBillingAccountChanged
            }}
        >
            {children}
        </AirtimePlanningContext.Provider>
    );
};

export { AirtimePlanningContext, AirtimePlanningProvider };
